import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusTimeline, { getProgressPercent } from "@/components/common/StatusTimeline";
import StatusBadge from "@/components/common/StatusBadge";
import InlineDocViewer from "@/components/common/InlineDocViewer";
import MandatoryVideo from "@/components/common/MandatoryVideo";
import ExamResultDetail from "@/components/common/ExamResultDetail";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- ExternalLink & Copy kept for the commented-out Exam Portal Access card
import { FileText, Clock, Download, GraduationCap, BookOpen, CreditCard, Sparkles, ArrowRight, ExternalLink, CheckCircle2, XCircle, Copy, Loader2, Lock } from "lucide-react";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import api from "@/services/api";

const NEXT_ACTIONS: Record<string, { text: string; description: string; icon: any }> = {
  pending: { text: "Application Under Review", description: "Your application is being reviewed by our team. We'll notify you once documents are verified.", icon: Clock },
  document_verified: { text: "Awaiting Course Assignment", description: "Your documents are verified! A course will be assigned shortly.", icon: BookOpen },
  course_assigned: { text: "Course Assigned", description: "Your course has been assigned. Your administrator will guide you through the next steps.", icon: ArrowRight },
  eligible_for_exam: { text: "Complete Your Exam", description: "You are eligible to take your exam. Click 'Start Exam' below to begin.", icon: FileText },
  exception: { text: "Awaiting ACA Review", description: "No exam is required for your application — your administrator will send it for ACA approval.", icon: Clock },
  sent_for_approval: { text: "Under ACA Review", description: "Your application is being reviewed by the ACA committee.", icon: Clock },
  approved: { text: "Payment Required", description: "Congratulations! Complete payment to receive your certificate.", icon: CreditCard },
  certified: { text: "You're ACA Certified! 🎓", description: "Congratulations! Download your certificate from the Certificates page.", icon: GraduationCap },
  paid: { text: "You're ACA Certified! 🎓", description: "Congratulations! Download your certificate from the Certificates page.", icon: GraduationCap },
};

const ProgressRing = ({ percent }: { percent: number }) => {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24" viewBox="0 0 96 96">
        <circle className="progress-ring-track" cx="48" cy="48" r={radius} />
        <circle
          className="progress-ring-fill"
          cx="48"
          cy="48"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ "--progress-offset": offset } as any}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold text-foreground">{percent}%</span>
      </div>
    </div>
  );
};

const StudentDashboard = () => {
  const { user, fullName } = useAuth();
  const [application, setApplication] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [examAssignments, setExamAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [examVideoUrl, setExamVideoUrl] = useState("");
  const [examVideoWatched, setExamVideoWatched] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const { data: app } = await supabase
        .from("applications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setApplication(app);

      if (app) {
        const { data: docs } = await supabase.from("documents").select("*").eq("application_id", app.id);
        setDocuments(docs || []);
      }

      // Load exam assignments (by user_id, works even before application exists)
      const { data: exams } = await supabase
        .from("exam_assignments")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });
      setExamAssignments(exams || []);

      // Mandatory exam intro video: URL + whether this student has already watched it.
      try {
        const res = await api.get("/exam-video");
        setExamVideoUrl(res.data?.url || "");
        setExamVideoWatched(!!res.data?.watched);
      } catch {
        /* if it fails, don't hard-block the student */
        setExamVideoUrl("");
        setExamVideoWatched(true);
      }

      setLoading(false);
    };
    fetchData();
  }, [user]);

  const handleVideoComplete = async () => {
    setExamVideoWatched(true);
    try { await api.post("/exam-video/complete"); } catch { /* best-effort persist */ }
  };

  // Gate the Start Exam button: locked while a video is configured and not yet watched.
  const examLocked = !!examVideoUrl && !examVideoWatched;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for the commented-out Exam Portal Access card
  const thinkExamPassword = user ? `Aca@${user.id.replace(/-/g, '').slice(0, 8)}` : '';

  // Launch an exam via the passwordless one-time login URL from ThinkExam.
  const handleStartExam = async (exam: any) => {
    if (!application) return;
    // Open the tab synchronously (within the click gesture) so popup blockers allow it,
    // then point it at the one-time URL once the API responds.
    const win = window.open("", "_blank");
    setLaunchingId(exam.id);
    try {
      const res = await api.post(`/think-exam/launch/${application.id}`, { testId: exam.think_exam_test_id });
      const url = res.data?.url;
      if (!url) throw new Error("No exam URL was returned. Please try again or contact your administrator.");
      if (win) win.location.href = url;
      else window.location.href = url;
    } catch (e: any) {
      if (win) win.close();
      toast({ title: "Could not start exam", description: e.message, variant: "destructive" });
    } finally {
      setLaunchingId(null);
    }
  };

  // "Open Exam Portal" — log the student straight into ThinkExam via the one-time
  // passwordless loginUrl (generated by unifiedAuth), using their first assigned exam.
  // Falls back to the manual candidate login page only if nothing is assigned.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for the commented-out Exam Portal Access card
  const handleOpenPortal = () => {
    const firstAssigned = examAssignments.find((e: any) => e.status === "assigned");
    if (firstAssigned) handleStartExam(firstAssigned);
    else window.open("https://exam.acaglobal.my/candidate/login", "_blank", "noopener");
  };

  const effectiveStatus = application?.payment_status === "paid" ? "certified" : application?.status;
  const progress = effectiveStatus ? getProgressPercent(effectiveStatus) : 0;
  const isCertified = effectiveStatus === "certified" || effectiveStatus === "paid";
  const firstName = fullName?.split(" ")[0] || "Student";
  const initials = fullName?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) || "ST";
  const nextAction = NEXT_ACTIONS[effectiveStatus] || NEXT_ACTIONS.pending;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="glass-panel rounded-2xl p-6 md:p-7 animate-fade-in">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full gradient-warm text-lg font-bold text-white shadow-md">
                {initials}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#7450a9]">Student Portal</p>
                <h2 className="mt-1.5 text-2xl font-bold tracking-tight text-foreground">Welcome back, {firstName}! 👋</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {isCertified
                    ? "Congratulations! You are now ACA Certified 🎓"
                    : `You are ${progress}% closer to becoming ACA Certified`}
                </p>
                {isCertified && (
                  <div className="mt-3">
                    <span className="certified-badge inline-flex items-center gap-2">
                      <Sparkles className="h-4 w-4 animate-sparkle" />
                      ACA Certified Member
                    </span>
                  </div>
                )}
              </div>
            </div>
            {application && (
              <div className="shrink-0">
                <ProgressRing percent={progress} />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !application ? (
          <Card className="glass-premium animate-fade-up">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-primary/60" />
              </div>
              <h3 className="text-lg font-medium text-foreground">No application yet</h3>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm">
                Your GrowLearn administrator will submit an application on your behalf. Please contact them to get started.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left Column */}
            <div className="flex flex-col gap-6">
              {/* Application Overview */}
              <Card className="glass-premium shadow-sm animate-fade-up" style={{ animationDelay: "0.1s" }}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3">Application Overview</h3>
                    <StatusBadge status={effectiveStatus} />
                  </div>
                  <div className="grid grid-cols-2 gap-0">
                    {[
                      { label: "App ID",        value: application.id.slice(0, 8), mono: true },
                      { label: "Submitted",     value: format(new Date(application.created_at), "MMM d, yyyy") },
                      { label: "Qualification", value: application.qualification || "—" },
                      { label: "Payment",       value: application.payment_status === "paid" ? "Paid" : "Unpaid",
                        green: application.payment_status === "paid" },
                    ].map(({ label, value, mono, green }, i) => (
                      <div key={label} className={`py-4 ${i % 2 === 0 ? "pr-6 border-r border-border/30" : "pl-6"} ${i < 2 ? "border-b border-border/30" : ""}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                        <p className={`text-sm font-semibold ${mono ? "font-mono" : ""} ${green ? "text-green-600" : "text-foreground"}`}>{value}</p>
                      </div>
                    ))}
                  </div>
                  {application.reviewer_notes && (
                    <div className="mt-5 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border-l-[3px] border-amber-400">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1">Reviewer Notes</p>
                      <p className="text-sm text-foreground">{application.reviewer_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Uploaded Documents */}
              <Card className="glass-premium shadow-sm animate-fade-up" style={{ animationDelay: "0.2s" }}>
                <CardContent className="p-6">
                  <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Uploaded Documents</h3>
                  {documents.length > 0 ? (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/10">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <FileText className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-foreground truncate">{doc.file_name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{doc.document_type}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <InlineDocViewer filePath={doc.file_path} fileName={doc.file_name} />
                            <Button
                              size="sm" variant="outline" title="Download"
                              className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
                              onClick={async () => {
                                const { data } = await supabase.storage
                                  .from("application-documents")
                                  .createSignedUrl(doc.file_path, 3600, { download: true });
                                if (data?.signedUrl) {
                                  const a = document.createElement("a");
                                  a.href = data.signedUrl;
                                  a.download = doc.file_name;
                                  a.click();
                                }
                              }}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center py-8 text-center border rounded-xl bg-muted/10">
                      <FileText className="h-6 w-6 text-muted-foreground/30 mb-2" />
                      <p className="text-xs text-muted-foreground">No documents uploaded yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Next Step */}
              <Card className="glass-premium shadow-sm animate-fade-up flex-1" style={{ animationDelay: "0.25s" }}>
                <CardContent className="p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-l-[3px] border-primary pl-3 mb-4">Next Step</p>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl gradient-warm flex items-center justify-center shrink-0">
                      <nextAction.icon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">{nextAction.text}</p>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{nextAction.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Exam Portal Credentials — shown when eligible_for_exam */}
              {effectiveStatus === "eligible_for_exam" && examAssignments.length > 0 && (
                <>
                  {/* Exam Portal Access card hidden for now — students launch via "Start Exam"
                      in the Your Exams card below. Un-comment this whole <Card> to restore it. */}
                  {/*
                  <Card className="glass-premium shadow-sm animate-fade-up border-primary/20" style={{ animationDelay: "0.3s" }}>
                    <CardContent className="p-6">
                      <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Exam Portal Access</h3>
                      <div className="space-y-3 mb-5">
                        {[
                          { label: "Portal", value: "exam.acaglobal.my", isLink: true },
                          { label: "Email", value: user?.email || "", copyable: true },
                          { label: "Password", value: thinkExamPassword, copyable: true },
                        ].map(({ label, value, isLink, copyable }) => (
                          <div key={label} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 border border-border/40">
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
                              {isLink ? (
                                <a
                                  href="https://exam.acaglobal.my/candidate/login"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-mono font-semibold text-primary hover:underline"
                                >
                                  {value}
                                </a>
                              ) : (
                                <p className="text-sm font-mono font-semibold text-foreground truncate">{value}</p>
                              )}
                            </div>
                            {copyable && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 shrink-0"
                                onClick={() => {
                                  navigator.clipboard.writeText(value);
                                  toast({ title: `${label} copied` });
                                }}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button
                        className="w-full gradient-warm text-white border-0 gap-2"
                        disabled={launchingId != null}
                        onClick={handleOpenPortal}
                      >
                        {launchingId != null
                          ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening…</>
                          : <><ExternalLink className="h-4 w-4" /> Open Exam Portal</>}
                      </Button>
                    </CardContent>
                  </Card>
                  */}

                  {examLocked && (
                    <Card className="glass-premium shadow-sm animate-fade-up border-primary/20" style={{ animationDelay: "0.3s" }}>
                      <CardContent className="p-6">
                        <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-3">Mandatory Briefing</h3>
                        <p className="text-sm text-muted-foreground mb-4">Please watch this short video in full before starting your exam.</p>
                        <MandatoryVideo src={examVideoUrl} onComplete={handleVideoComplete} />
                      </CardContent>
                    </Card>
                  )}

                  <Card className="glass-premium shadow-sm animate-fade-up" style={{ animationDelay: "0.35s" }}>
                    <CardContent className="p-6">
                      <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Your Exams</h3>
                      <div className="space-y-3">
                        {examAssignments.filter((exam: any) => exam.status !== "reassigned").map((exam: any) => (
                          <div
                            key={exam.id}
                            className={`rounded-xl border p-4 ${exam.status === "passed" ? "border-green-200 bg-green-50/50 dark:bg-green-950/10" : exam.status === "failed" ? "border-red-200 bg-red-50/50 dark:bg-red-950/10" : "border-border/60 bg-muted/10"}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${exam.status === "passed" ? "bg-green-100" : exam.status === "failed" ? "bg-red-100" : "bg-primary/10"}`}>
                                  {exam.status === "passed"
                                    ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    : exam.status === "failed"
                                    ? <XCircle className="h-4 w-4 text-red-500" />
                                    : <FileText className="h-4 w-4 text-primary" />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-foreground truncate">{exam.think_exam_test_name || exam.think_exam_test_id}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">{exam.think_exam_group_name}</p>
                                  {exam.status === "passed" && exam.score != null && (
                                    <p className="text-xs text-green-600 font-medium mt-1">Score: {exam.score}/{exam.total_marks} ({exam.percentage}%)</p>
                                  )}
                                  {exam.status === "failed" && exam.score != null && (
                                    <p className="text-xs text-red-600 font-medium mt-1">Score: {exam.score}/{exam.total_marks} ({exam.percentage}%)</p>
                                  )}
                                  {exam.status === "failed" && (
                                    <p className="text-xs text-muted-foreground mt-1">Contact your administrator to request a reassignment.</p>
                                  )}
                                </div>
                              </div>
                              <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${exam.status === "passed" ? "bg-green-100 text-green-700" : exam.status === "failed" ? "bg-red-100 text-red-700" : "bg-primary/10 text-primary"}`}>
                                {exam.status === "passed" ? "Passed" : exam.status === "failed" ? "Failed" : "Assigned"}
                              </span>
                            </div>
                            {(exam.status === "passed" || exam.status === "failed") && <ExamResultDetail exam={exam} />}
                            {exam.status === "assigned" && (
                              <Button
                                size="sm"
                                className="w-full mt-3 gradient-warm text-white border-0 gap-2"
                                disabled={examLocked || launchingId === exam.id}
                                onClick={() => handleStartExam(exam)}
                              >
                                {examLocked
                                  ? <><Lock className="h-4 w-4" /> Watch the briefing video to unlock</>
                                  : launchingId === exam.id
                                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</>
                                  : <><ArrowRight className="h-4 w-4" /> Start Exam</>}
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Right Column */}
            <div className="flex flex-col">
              {/* Application Journey */}
              <Card className="glass-premium shadow-sm animate-fade-up flex-1" style={{ animationDelay: "0.15s" }}>
                <CardContent className="p-6">
                  <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-5">Application Journey</h3>
                  <StatusTimeline currentStatus={effectiveStatus} />
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;
